import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

// Styled Components
const HomeContainer = styled.div`
  width: 100%;
`;

const HeroSection = styled.section`
  padding: 5rem 0;
  background: linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), 
              url('/images/backgrounds/paris-travel.jpg'); /* Using public folder path */
  background-size: cover;
  background-position: center;
  color: var(--text);
  min-height: 70vh;
  display: flex;
  align-items: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Headline = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: var(--text);
  
  span {
    color: var(--accent-turquoise);
  }
`;

const Subhead = styled.p`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  max-width: 700px;
  color: var(--text-light);
`;

const QuickPitch = styled.p`
  font-size: 1.25rem;
  margin-bottom: 2.5rem;
  max-width: 600px;
  color: var(--text-light);
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const PrimaryButton = styled(Link)`
  background-color: var(--accent-turquoise);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  border: 2px solid var(--accent-turquoise);
  transition: all 0.3s ease;
  
  &:hover {
    background-color: transparent;
    color: var(--text);
    border-color: var(--accent-turquoise);
    transform: scale(1.05);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const SecondaryButton = styled(Link)`
  background-color: transparent;
  color: var(--text);
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  border: 2px solid var(--accent-lavender);
  transition: all 0.3s ease;
  
  &:hover {
    background-color: transparent;
    color: var(--text);
    transform: scale(1.05);
  }
`;

// Service section styles
const ServiceSection = styled.section`
  padding: 5rem 0;
  background-color: ${props => props.bgColor || 'white'};
`;

const SectionHeading = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  text-align: center;
  
  span {
    color: ${props => props.accentColor || 'var(--accent-turquoise)'};
  }
`;

const SectionDescription = styled.p`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  font-size: 1.2rem;
  color: var(--text-light);
`;

const ServiceContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ServiceInfo = styled.div`
  order: ${props => props.reverse ? 2 : 1};
  
  @media (max-width: 968px) {
    order: 1;
  }
`;

const ServiceFeatureList = styled.ul`
  margin: 2rem 0;
  padding-left: 1.5rem;
`;

const ServiceFeature = styled.li`
  margin-bottom: 1rem;
  font-size: 1.1rem;
`;

const CaseStudyCard = styled.div`
  order: ${props => props.reverse ? 1 : 2};
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
  
  @media (max-width: 968px) {
    order: 2;
  }
`;

const CaseStudyImage = styled.div`
  height: 250px;
  background-size: cover;
  background-position: center;
`;

const CaseStudyContent = styled.div`
  padding: 2rem;
`;

const CaseStudyTag = styled.span`
  display: inline-block;
  background-color: ${props => {
    switch(props.category) {
      case 'Travel Marketing': return 'rgba(62, 221, 226, 0.1)';
      case 'Tourism Development': return 'rgba(123, 123, 243, 0.1)';
      case 'Digital Tools': return 'rgba(123, 211, 244, 0.1)';
      default: return 'rgba(62, 221, 226, 0.1)';
    }
  }};
  color: ${props => {
    switch(props.category) {
      case 'Travel Marketing': return 'var(--accent-turquoise)';
      case 'Tourism Development': return 'var(--accent-lavender)';
      case 'Digital Tools': return 'var(--accent-blue)';
      default: return 'var(--accent-turquoise)';
    }
  }};
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const CaseStudyTitle = styled.h3`
  font-size: 1.75rem;
  margin-bottom: 1rem;
`;

const CaseStudyStat = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
  color: var(--accent-lavender);
  margin: 1rem 0;
`;

const SectionButton = styled(Link)`
  display: block;
  text-align: center;
  margin: 3rem auto 0;
  width: fit-content;
  background-color: ${props => props.bgColor || 'var(--accent-turquoise)'};
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid ${props => props.bgColor || 'var(--accent-turquoise)'};
  transition: all 0.3s ease;
  
  &:hover {
    background-color: transparent;
    color: var(--text);
  }
`;

// CTASection
const CTASection = styled.section`
  padding: 5rem 0;
  background-color: var(--secondary);
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
`;

const CTADescription = styled.p`
  max-width: 800px;
  margin: 0 auto 2rem;
  font-size: 1.2rem;
`;

const Home = () => {
  return (
    <HomeContainer>
      <HeroSection>
        <Container>
          <Headline>Building What's Next for <span>Travel Innovators</span>.</Headline>
          <Subhead>From Paris, we craft smarter marketing, partnerships, and tools for travel brands ready to lead.</Subhead>
          <QuickPitch>We're your R&D team—without the overhead. Growth strategies, connections, and tools that make travel better.</QuickPitch>
          <ButtonContainer>
            <PrimaryButton to="/projects">Explore Our Experiments</PrimaryButton>
            <SecondaryButton to="/contact">Start Your Project</SecondaryButton>
          </ButtonContainer>
        </Container>
      </HeroSection>
      
      {/* Travel Marketing Section */}
      <ServiceSection>
        <Container>
          <SectionHeading accentColor="var(--accent-turquoise)">Travel <span>Marketing</span></SectionHeading>
          <SectionDescription>
            Data-driven marketing strategies that connect authentic travel experiences with the right audiences.
          </SectionDescription>
          
          <ServiceContentGrid>
            <ServiceInfo>
              <h3>Authentic Stories, Real Growth</h3>
              <p>
                Our Travel Marketing services help small to medium travel operators craft compelling stories 
                and implement effective campaigns that respect your values while driving meaningful results.
              </p>
              
              <ServiceFeatureList>
                <ServiceFeature>Email campaigns that achieve 50%+ open rates</ServiceFeature>
                <ServiceFeature>Content strategies that rank in competitive markets</ServiceFeature>
                <ServiceFeature>Strategic partnerships that expand your reach</ServiceFeature>
                <ServiceFeature>Brand development rooted in authentic experiences</ServiceFeature>
              </ServiceFeatureList>
              
              <PrimaryButton to="/travel-marketing">Explore Travel Marketing</PrimaryButton>
            </ServiceInfo>
            
            <CaseStudyCard>
              <Link to="/projects/kikooko-seo">
                <CaseStudyImage style={{ backgroundImage: `url('/images/projects/kikooko-seo.webp')` }} />
              </Link>
              <CaseStudyContent>
                <CaseStudyTag category="Travel Marketing">Travel Marketing</CaseStudyTag>
                <CaseStudyTitle>Kikooko Africa Safaris</CaseStudyTitle>
                <p>How targeted email campaigns and partnerships doubled bookings for this Uganda-based safari company.</p>
                <CaseStudyStat>Email + partnerships = 2x bookings in 6 months</CaseStudyStat>
                <Link to="/projects/kikooko-seo" style={{ color: 'var(--accent-turquoise)', fontWeight: 600 }}>
                  View Case Study →
                </Link>
              </CaseStudyContent>
            </CaseStudyCard>
          </ServiceContentGrid>
        </Container>
      </ServiceSection>
      
      {/* Tourism Development Section */}
      <ServiceSection bgColor="var(--secondary)">
        <Container>
          <SectionHeading accentColor="var(--accent-lavender)">Tourism <span>Development</span></SectionHeading>
          <SectionDescription>
            Building bridges between local operators and global markets through sustainable, community-centered approaches.
          </SectionDescription>
          
          <ServiceContentGrid>
            <ServiceInfo reverse={true}>
              <h3>Meaningful Connections, Sustainable Growth</h3>
              <p>
                Our Tourism Development work empowers communities and small operators to meaningfully connect with 
                global markets while preserving local culture and environment.
              </p>
              
              <ServiceFeatureList>
                <ServiceFeature>Familiarization trips that create lasting partnerships</ServiceFeature>
                <ServiceFeature>Destination strategies balancing economic & social benefits</ServiceFeature>
                <ServiceFeature>Capacity building programs for local tourism stakeholders</ServiceFeature>
                <ServiceFeature>Evidence-based policy development for sustainable tourism</ServiceFeature>
              </ServiceFeatureList>
              
              <PrimaryButton to="/tourism-development">Explore Tourism Development</PrimaryButton>
            </ServiceInfo>
            
            <CaseStudyCard reverse={true}>
              <Link to="/projects/eswatini-training">
                <CaseStudyImage style={{ backgroundImage: `url('/images/projects/eswatini-training.webp')` }} />
              </Link>
              <CaseStudyContent>
                <CaseStudyTag category="Tourism Development">Tourism Development</CaseStudyTag>
                <CaseStudyTitle>Eswatini Tourism</CaseStudyTitle>
                <p>How comprehensive digital marketing training achieved 95% satisfaction rates among tourism stakeholders.</p>
                <CaseStudyStat>95% satisfaction rate, 2.5 point skill improvement</CaseStudyStat>
                <Link to="/projects/eswatini-training" style={{ color: 'var(--accent-turquoise)', fontWeight: 600 }}>
                  View Case Study →
                </Link>
              </CaseStudyContent>
            </CaseStudyCard>
          </ServiceContentGrid>
        </Container>
      </ServiceSection>
      
      {/* Digital Tools Section */}
      <ServiceSection>
        <Container>
          <SectionHeading accentColor="var(--accent-blue)">Digital <span>Tools</span></SectionHeading>
          <SectionDescription>
            Custom software solutions that save time and enhance customer experiences for travel businesses.
          </SectionDescription>
          
          <ServiceContentGrid>
            <ServiceInfo>
              <h3>Efficient Systems, Human Experiences</h3>
              <p>
                Our Digital Tools are built specifically for the needs of small to medium travel operators,
                making your business more efficient without losing the human touch that makes your offerings special.
              </p>
              
              <ServiceFeatureList>
                <ServiceFeature>Itinerary builders that cut planning time by 60%</ServiceFeature>
                <ServiceFeature>Connection platforms that replace costly travel fairs</ServiceFeature>
                <ServiceFeature>Marketing automation tools for consistent messaging</ServiceFeature>
                <ServiceFeature>Custom solutions for unique business challenges</ServiceFeature>
              </ServiceFeatureList>
              
              <PrimaryButton to="/digital-tools">Explore Digital Tools</PrimaryButton>
            </ServiceInfo>
            
            <CaseStudyCard>
              <Link to="/projects/itinerary-generator">
                <CaseStudyImage style={{ backgroundImage: `url('/images/projects/share_itinerary.webp')` }} />
              </Link>
              <CaseStudyContent>
                <CaseStudyTag category="Digital Tools">Digital Tools</CaseStudyTag>
                <CaseStudyTitle>Itinerary Generator</CaseStudyTitle>
                <p>Custom tool development that dramatically cut planning time for multi-day tours.</p>
                <CaseStudyStat>Planning time reduced by 60%</CaseStudyStat>
                <Link to="/projects/itinerary-generator" style={{ color: 'var(--accent-turquoise)', fontWeight: 600 }}>
                  View Case Study →
                </Link>
              </CaseStudyContent>
            </CaseStudyCard>
          </ServiceContentGrid>
        </Container>
      </ServiceSection>
      
      <CTASection>
        <Container>
          <CTATitle>Ready to Experiment?</CTATitle>
          <CTADescription>
            Let's discuss how our team can help your travel business develop innovative solutions
            that respect your values while driving growth.
          </CTADescription>
          <PrimaryButton to="/contact">Start Your Project</PrimaryButton>
        </Container>
      </CTASection>
    </HomeContainer>
  );
};

export default Home;